const MAX_STYLES_ON_PAGE = 10;

const getListStyleIndex = ({ listStyle, displayListStyle, isLast }) => {
    //to retrieve the display list's first and last style index on list styles
    if (isLast) {
        return listStyle.findIndex((item: any) => item.id === displayListStyle[displayListStyle?.length - 1]?.id);
    } else {
        return listStyle.findIndex((item: any) => item.id === displayListStyle[0]?.id);
    }
};

const updateDisplayStyle = ({
    swiperInstance,
    forceUpdate = false,
    displayListStyle,
    setDisplayListStyle,
    listStyle,
    nextListDataUpdated,
    prevListDataUpdated,
}) => {
    const bufferIndex = 1;
    const swipeDownThreshold = 2;
    const lastCardIndex = displayListStyle.length - bufferIndex;
    const firstCardIndex = 0;
    const bufferStyleCount = 4;
    const slideAnimationSpeed = 0;
    const nextSlideSetInitialIndex = bufferStyleCount - bufferIndex;

    const getExistingStyles = (direction = 'end') =>
        direction === 'start'
            ? displayListStyle.slice(firstCardIndex, bufferStyleCount)
            : displayListStyle.slice(-bufferStyleCount);

    const getUpdatedTempStyles = (skipCount = bufferStyleCount) =>
        displayListStyle.filter((_, index) => index > skipCount - bufferIndex);

    const getPreviousStylesToAppend = (listStyleIndex) => {
        const firstStyleIndex = Math.max(listStyleIndex - MAX_STYLES_ON_PAGE + nextSlideSetInitialIndex, 0);
        return listStyle.slice(firstStyleIndex, listStyleIndex);
    };

    const handleSwipeForward = () => {
        if (
            swiperInstance.activeIndex > displayListStyle.length - nextSlideSetInitialIndex &&
            !nextListDataUpdated.current.status
        ) {
            const listStyleIndex = getListStyleIndex({
                listStyle,
                displayListStyle,
                isLast: true,
            });
            if (listStyle[listStyleIndex + bufferIndex]) {
                const existingStyles = getExistingStyles('end');
                const tempStyles = getUpdatedTempStyles();
                nextListDataUpdated.current.prevValue = displayListStyle;

                setDisplayListStyle([...existingStyles, ...tempStyles]);

                nextListDataUpdated.current.status = true;
                prevListDataUpdated.current.status = false;
            }
        }

        if (prevListDataUpdated.current.status) {
            setDisplayListStyle(prevListDataUpdated.current.prevValue);
            prevListDataUpdated.current.status = false;
        }
    };

    const handleSwipeBackward = () => {
        if (swiperInstance.activeIndex < swipeDownThreshold && !prevListDataUpdated.current.status) {
            const listStyleIndex = getListStyleIndex({
                listStyle,
                displayListStyle,
                isLast: false,
            });
            if (listStyle[listStyleIndex - bufferIndex]) {
                const existingStyles = getExistingStyles('start');
                const appendStyleList = getPreviousStylesToAppend(listStyleIndex);
                const tempDisplayStyle = appendStyleList.filter((_, index) => index > nextSlideSetInitialIndex);
                prevListDataUpdated.current.prevValue = displayListStyle;
                setDisplayListStyle([...existingStyles, ...tempDisplayStyle, ...existingStyles]);
                nextListDataUpdated.current.status = false;
                prevListDataUpdated.current.status = true;
            }
        }

        if (nextListDataUpdated.current.status) {
            setDisplayListStyle(nextListDataUpdated.current.prevValue);
            nextListDataUpdated.current.status = false;
        }
    };

    const handleForceUpdate = () => {
        const existingStyles = getExistingStyles('end');
        const listStyleIndex = getListStyleIndex({
            listStyle,
            displayListStyle,
            isLast: true,
        });
        if (listStyleIndex < listStyle.length - bufferIndex) {
            const appendStyleList = listStyle.slice(
                listStyleIndex + bufferIndex,
                listStyleIndex + (MAX_STYLES_ON_PAGE - bufferStyleCount),
            );

            setDisplayListStyle([...existingStyles, ...appendStyleList]);
            swiperInstance?.slideTo(nextSlideSetInitialIndex, slideAnimationSpeed);
        }
    };

    const handleSlideToNext = () => {
        const existingStyles = getExistingStyles('end');
        const listStyleIndex = getListStyleIndex({
            listStyle,
            displayListStyle,
            isLast: true,
        });
        if (listStyleIndex < listStyle.length - bufferIndex) {
            const appendStyleList = listStyle.slice(
                listStyleIndex + bufferIndex,
                listStyleIndex + (MAX_STYLES_ON_PAGE - nextSlideSetInitialIndex),
            );

            setTimeout(() => {
                swiperInstance?.slideTo(nextSlideSetInitialIndex, slideAnimationSpeed);
                setDisplayListStyle([...existingStyles, ...appendStyleList]);
                nextListDataUpdated.current.status = false;
            }, 300);
        }
    };

    const handleSlideToPrev = () => {
        const listStyleIndex = getListStyleIndex({
            listStyle,
            displayListStyle,
            isLast: false,
        });
        if (listStyle[listStyleIndex - bufferIndex]) {
            const existingStyles = getExistingStyles('start');
            const appendStyleList = getPreviousStylesToAppend(listStyleIndex);

            setTimeout(() => {
                swiperInstance?.slideTo(displayListStyle.length - bufferStyleCount, slideAnimationSpeed);
                setDisplayListStyle([...appendStyleList, ...existingStyles]);
                prevListDataUpdated.current.status = false;
            }, 300);
        }
    };

    // === MAIN SWITCH HANDLING ===
    if (
        swiperInstance.activeIndex > swiperInstance.previousIndex &&
        swiperInstance.activeIndex - swiperInstance.previousIndex === bufferIndex
    ) {
        handleSwipeForward();
    } else if (
        swiperInstance.activeIndex < swiperInstance.previousIndex &&
        swiperInstance.previousIndex - swiperInstance.activeIndex === bufferIndex
    ) {
        handleSwipeBackward();
    }

    if (forceUpdate) {
        handleForceUpdate();
    } else if (swiperInstance.activeIndex === lastCardIndex) {
        handleSlideToNext();
    } else if (swiperInstance.activeIndex === firstCardIndex) {
        handleSlideToPrev();
    }
};

export const SwiperUtils = {
    updateDisplayStyle,
    getListStyleIndex,
    MAX_STYLES_ON_PAGE,
};
